import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SignUpModal from './SignUpModal';

// SVG Icons for a clean, modern look
const SearchIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
);

const BreakdownIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
        <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
    </svg>
);

const VerifiedIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
);

const SubmitReviewIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
);

const StarIcon = ({ className }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
    </svg>
);

const ArrowForwardIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"></line>
        <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
);

const QuoteIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 17h3l2-4V7H5v6h3l-2 4zm8 0h3l2-4V7h-6v6h3l-2 4z"></path>
    </svg>
);

const CheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);


// Home Page Component
const HomePage = ({}) => {
    const navigate = useNavigate();
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const location = useLocation();
    const testimonials = [
        { name: "Sarah M.", role: "UC Graduate Student", quote: "I almost signed a lease until I saw the hidden pet fees on RentRadar. Saved me hundreds!", rating: 5 },
        { name: "Mike R.", role: "Working Professional", quote: "My landlord didn’t fix our AC for 3 weeks—glad I posted about it to warn others.", rating: 5 },
        { name: "Jessica L.", role: "UC Undergraduate", quote: "The community data helped me budget accurately for off-campus housing. No more surprises.", rating: 5 }
    ];

    const steps = [
        { icon: <SearchIcon />, title: "Discover Your Options", description: "Search for apartments and see what real renters have to say." },
        { icon: <BreakdownIcon />, title: "Understand the Costs", description: "Get a clear, transparent breakdown of rent, utilities, and fees." },
        { icon: <VerifiedIcon />, title: "Read Honest Reviews", description: "Learn from the experiences of verified tenants before you sign." },
        { icon: <SubmitReviewIcon />, title: "Submit Your Review", description: "Share your own experience to improve the accuracy of our data." }
    ];
    
    const uniqueFeatures = [
        { title: "Verified Reviews", text: "Every review is checked to ensure it comes from a real tenant." },
        { title: "Transparent Costs", text: "See the full picture of what you'll pay, with no surprises." },
        { title: "Data-Driven Insights", text: "We analyze trends to help you understand the market better." },
        { title: "Uncensored Feedback", text: "Honest opinions, both good and bad, are welcome and shared." },
        { title: "Privacy First", text: "Share your experience anonymously. Your privacy is protected." },
        { title: "Advanced Filtering", text: "Filter by safety, price, walkability, and more."}
    ];
    
    // --- FADE-IN ANIMATION LOGIC ---
    const sectionsRef = useRef([]);
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );

        sectionsRef.current.forEach((section) => {
            if (section) {
                observer.observe(section);
            }
        });

        return () => {
            sectionsRef.current.forEach((section) => {
                if (section) {
                    observer.unobserve(section);
                }
            });
        };
    }, []);


    return (
        <>
            {/* --- CSS STYLES --- */}
            <style>{`
            /* --- Font Imports --- */
            @import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&family=Lato:wght@400;700&display=swap');

            /* --- CSS Variables for Trustworthy Theme --- */
            :root {
                --bg-light: #F7FAFC;
                --bg-dark: #1A202C;
                --primary: #2C5282; /* Dependable Blue */
                --secondary: #2F855A; /* Warm Green */
                --accent: #DD6B20; /* Warm Orange */
                --text-light: #EDF2F7;
                --text-dark: #2D3748;
                --card-light: #FFFFFF;
                --card-dark: #2D3748;
                --font-heading: 'Merriweather', serif;
                --font-body: 'Lato', sans-serif;
            }

            /* --- Base Styles --- */
            body { margin: 0; }
            .homepage {
                font-family: var(--font-body);
                color: var(--text-dark);
                background-color: var(--bg-light);
                transition: background-color 0.3s, color 0.3s;
            }
            .dark-mode .homepage {
                background-color: var(--bg-dark);
                color: var(--text-light);
            }
            .section { padding: 6rem 1.5rem; }
            .container { max-width: 1100px; margin: 0 auto; }
            .fade-in-section {
                opacity: 0;
                transform: translateY(30px);
                transition: opacity 0.6s ease-out, transform 0.6s ease-out;
            }
            .fade-in-section.visible { opacity: 1; transform: translateY(0); }

            /* --- Typography --- */
            .section-title {
                font-family: var(--font-heading);
                font-size: 2.5rem;
                font-weight: 900;
                text-align: center;
                margin-bottom: 4rem;
                color: var(--primary);
            }
            .dark-mode .section-title { color: var(--text-light); }

            /* --- Hero Section --- */
            .info-button {
                position: absolute;
                top: 2rem;
                right: 2rem;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background-color: #e77e2e21;
                color: #e77e2e;
                border: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1.2rem;
                font-weight: bold;
                transition: transform 0.2s, box-shadow 0.2s;
            }
            .info-button:hover {
                transform: scale(1.1);
                box-shadow: 0 4px 12px rgba(221, 107, 32, 0.4);
            }
            .hero-section {
                position: relative;
                text-align: center;
                background-color: #EDF2F7;
            }

            .hero-section {
                text-align: center;
                background-color: #EDF2F7;
            }
            .dark-mode .hero-section { background-color: #2D3748; }
            .hero-title {
                font-family: var(--font-heading);
                font-size: 3.2rem;
                font-weight: 900;
                line-height: 1.2;
                margin-bottom: 1.5rem;
            }
            .hero-title .highlight {
                color: var(--accent);
            }
            .hero-subtitle {
                font-size: 1.15rem;
                color: #4A5568;
                max-width: 650px;
                margin: 0 auto 2.5rem auto;
                line-height: 1.7;
            }
            .dark-mode .hero-subtitle { color: #A0AEC0; }

            /* --- How It Works Section --- */
            .steps-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 2rem;
                position: relative;
            }
            .step-card {
                text-align: center;
                padding: 2rem;
                border-radius: 12px;
                background-color: var(--card-light);
                border: 1px solid #E2E8F0;
                box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
                transition: transform 0.3s ease, box-shadow 0.3s ease;
            }
            .step-card:hover {
                transform: translateY(-8px);
                box-shadow: 0 10px 15px -3px rgba(0,0,0,0.07), 0 4px 6px -2px rgba(0,0,0,0.04);
            }
            .dark-mode .step-card {
                background-color: var(--card-dark);
                border-color: #4A5568;
            }
            .step-number {
                font-family: var(--font-heading);
                font-size: 1.5rem;
                font-weight: 700;
                color: var(--accent);
                margin-bottom: 1rem;
            }
            .step-icon-wrapper { color: var(--primary); margin-bottom: 1rem; }
            .dark-mode .step-icon-wrapper { color: var(--secondary); }
            .step-title { font-size: 1.2rem; font-weight: 700; margin-bottom: 0.5rem; }
            .step-description { color: #4A5568; line-height: 1.6; }
            .dark-mode .step-description { color: #A0AEC0; }
            
            /* --- Why It's Needed Section --- */
            .why-needed-section { background-color: #EDF2F7; }
            .dark-mode .why-needed-section { background-color: #2D3748; }
            .why-needed-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 4rem;
                align-items: flex-start;
            }
            .why-needed-content h3 {
                font-family: var(--font-heading);
                font-size: 1.8rem;
                margin-bottom: 1.5rem;
                color: var(--primary);
            }
             .dark-mode .why-needed-content h3 { color: var(--text-light); }
            .why-needed-content p { line-height: 1.8; color: #4A5568; }
            .dark-mode .why-needed-content p { color: #A0AEC0; }
            .problem-list {
                list-style: none;
                padding-left: 0;
                margin-top: 1.5rem;
            }
            .problem-list li {
                background-color: #FED7D7;
                color: #9B2C2C;
                padding: 0.75rem 1.25rem;
                border-radius: 8px;
                margin-bottom: 0.75rem;
                font-weight: 700;
                border-left: 4px solid #C53030;
            }


            /* --- Unique Features Section --- */
            .unique-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 2.5rem;
            }
            .unique-feature {
                display: flex;
                align-items: flex-start;
                gap: 1.5rem;
            }
            .unique-icon { color: var(--secondary); }
            .dark-mode .unique-icon { color: var(--secondary); }
            .unique-title { font-size: 1.1rem; font-weight: 700; margin: 0 0 0.5rem 0; }
            .unique-text { color: #4A5568; line-height: 1.6; margin: 0; }
            .dark-mode .unique-text { color: #A0AEC0; }

            /* --- Testimonials Section --- */
            .testimonials-section { background-color: #EDF2F7; }
            .dark-mode .testimonials-section { background-color: #2D3748; }
            .testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
            .testimonial-card {
                background: var(--card-light);
                padding: 2rem;
                border-radius: 12px;
                display: flex;
                flex-direction: column;
                border: 1px solid #E2E8F0;
                box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
            }
            .dark-mode .testimonial-card { background-color: var(--card-dark); border-color: #4A5568; }
            .testimonial-quote-icon { color: var(--accent); opacity: 0.5; margin-bottom: 1rem; }
            .testimonial-quote { font-style: italic; color: #4A5568; margin: 0 0 1.5rem 0; flex-grow: 1; }
            .dark-mode .testimonial-quote { color: #A0AEC0; }
            .testimonial-author { margin-top: auto; padding-top: 1.5rem; border-top: 1px solid #E2E8F0; }
            .dark-mode .testimonial-author { border-top-color: #4A5568; }
            .testimonial-name { font-weight: 700; margin: 0; }
            .testimonial-role { font-size: 0.9rem; color: #718096; margin: 0.25rem 0 1rem 0; }
            .dark-mode .testimonial-role { color: #A0AEC0; }
            .testimonial-rating { display: flex; gap: 0.25rem; }
            .star-filled { color: var(--accent); }

            /* --- CTA Section --- */
            .cta-section {
                color: var(--text-light);
                text-align: center;
                background-color: var(--primary);
            }
            .cta-title { color: var(--text-light); font-size: 2.25rem; }
            .cta-subtitle { font-size: 1.1rem; opacity: 0.9; margin-bottom: 2.5rem; max-width: 600px; margin-left: auto; margin-right: auto; }
            .cta-buttons, .hero-buttons { display: flex; justify-content: center; gap: 1.5rem; flex-wrap: wrap; }
            .cta-button {
                font-size: 1.05rem;
                font-weight: 700;
                padding: 0.9rem 2.5rem;
                border-radius: 8px;
                cursor: pointer;
                transition: transform 0.2s, box-shadow 0.2s;
                display: inline-flex;
                align-items: center;
                gap: 0.75rem;
                border: 2px solid transparent;
            }
            .cta-button:hover { transform: translateY(-3px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
            .cta-button.primary { background-color: var(--accent); color: var(--text-dark); }
            .dark-mode .cta-button.primary { background-color: var(--secondary); }
            .cta-button.secondary { background-color: transparent; color: var(--text-light); border-color: var(--text-light); }

            /* --- Placeholder Page --- */
            .placeholder-page {
                display: flex; flex-direction: column; justify-content: center; align-items: center;
                height: 100vh; font-family: var(--font-body); background-color: var(--bg-light); text-align: center;
            }
            .placeholder-page h1 { font-family: var(--font-heading); font-size: 2.5rem; color: var(--text-dark); }
            .placeholder-page p { font-size: 1.2rem; color: #4A5568; margin-bottom: 2rem; }
            .placeholder-page button {
                font-size: 1rem; font-weight: 700; padding: 0.8rem 2rem; border-radius: 8px; cursor: pointer;
                background-color: var(--primary); color: white; border: none; transition: background-color 0.3s;
            }
            .placeholder-page button:hover { background-color: #2C5282; }

            /* --- Responsive Adjustments --- */
            @media (max-width: 1024px) {
                .steps-grid, .testimonials-grid { grid-template-columns: repeat(2, 1fr); }
                .unique-grid, .why-needed-grid { grid-template-columns: 1fr; }
            }
            @media (max-width: 768px) {
                .hero-title { font-size: 2.5rem; }
                .section-title { font-size: 2rem; }
                .section { padding: 4rem 1.5rem; }
                .steps-grid, .testimonials-grid { grid-template-columns: 1fr; }
            }
           `}</style>

            {/* --- JSX STRUCTURE --- */}
            <div className="homepage">
                <div className="hero-section section">
                    <div className="container">
                        {/* <button className="info-button" onClick={() => setShowSignUpModal(true)}>
                            i
                        </button> */}
                        <h1 className="hero-title">
                            Understand Your <span className="highlight">True Cost</span> of Living
                        </h1>
                        <p className="hero-subtitle">
                            Rent Radar is a platform for transparent apartment reviews and real cost breakdowns. Make your next move the right move.                        </p>
                    </div>
                </div>

                <div className="how-it-works-section section fade-in-section" ref={(el) => (sectionsRef.current[0] = el)}>
                <div className="container">
                    <h2 className="section-title">Clarity in Four Simple Steps</h2>
                    <div className="steps-grid">
                        {steps.map((step, index) => (
                            <div className="step-card" key={index}>
                                <div className="step-number">0{index + 1}</div>
                                <div className="step-icon-wrapper">{step.icon}</div>
                                <h3 className="step-title">{step.title}</h3>
                                <p className="step-description">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
                
                <div className="section fade-in-section" ref={(el) => (sectionsRef.current[1] = el)}>
                    <div className="container">
                        <h2 className="section-title">Why RentRadar Is Needed</h2>
                        <div className="why-needed-grid">
                            <div className="why-needed-content">
                                <h3>Tired of Rental Nightmares?</h3>
                                <p>The rental market is broken. Landlords aren't always honest about total costs, and generic review sites don't focus on what renters truly need. Too many people feel trapped after moving in due to:</p>
                                <ul className="problem-list">
                                    <li>Unexpected fees and inflated utility bills</li>
                                    <li>Unresponsive landlords and poor maintenance</li>
                                    <li>Pests, bad plumbing, or noisy neighbors</li>
                                </ul>
                            </div>
                            <div className="why-needed-content">
                                <h3>Power to the People.</h3>
                                <p>RentRadar is different. We believe in transparency, built by a community of renters for renters. By sharing honest, detailed, and crowdsourced data, we empower you to avoid bad deals and find a place you'll genuinely love.</p>
                                <p><strong>It's time to make smarter decisions, together.</strong></p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="unique-section section fade-in-section" ref={(el) => (sectionsRef.current[2] = el)}>
                    <div className="container">
                        <h2 className="section-title">What Makes Us Different</h2>
                        <div className="unique-grid">
                            {uniqueFeatures.map((feature, index) => (
                                <div className="unique-feature" key={index}>
                                    <div className="unique-icon"><CheckIcon /></div>
                                    <div>
                                        <h3 className="unique-title">{feature.title}</h3>
                                        <p className="unique-text">{feature.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="cta-section section">
                    <div className="container">
                        <h2 className="cta-title">Be Part of the Movement for Rent Transparency</h2>
                        <p className="cta-subtitle">Find your next home with confidence, or help others by sharing your experience.</p>
                        <div className="cta-buttons">
                             <button className="cta-button primary" onClick={() => navigate('search')}>
                                Start Searching <ArrowForwardIcon />
                            </button>
                             <button className="cta-button secondary" onClick={() => navigate('submit')}>
                                Leave a Review
                            </button>
                        </div>
                    </div>
                </div>
                {showSignUpModal && (
                    <SignUpModal
                        isOpen={showSignUpModal}
                        onClose={() => setShowSignUpModal(false)}
                    />
                )}
            </div>
        </>
    );
};

export default HomePage;
